import React, { useEffect, useState } from "react";
import { MapPin, ArrowRight, X } from "lucide-react";
import { getCityFact } from "@/data/cityFacts";
import { flagEmoji } from "@/lib/globeSearch";

const API = process.env.REACT_APP_BACKEND_URL;
const cache = new Map();

/** Fact + photo card for a searched/tapped city. `place` = { name, code, country, kind }. */
export default function PlaceCard({ place, onOpenCountry, onClose, compact = false }) {
  const [info, setInfo] = useState(null);
  const countryName = place.country?.name || "";
  const fact = place.kind === "country" ? null : getCityFact(place.name);
  useEffect(() => {
    let live = true;
    if (place.kind === "country") { setInfo({ none: true }); return undefined; }
    const key = `${place.name}|${countryName}`;
    if (cache.has(key)) { setInfo(cache.get(key)); return undefined; }
    setInfo(null);
    fetch(`${API}/api/place/info?name=${encodeURIComponent(place.name)}&country=${encodeURIComponent(countryName)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!live) return; if (j && !j.transient) cache.set(key, j); setInfo(j || { none: true }); })
      .catch(() => live && setInfo({ none: true }));
    return () => { live = false; };
  }, [place.name, countryName]);
  const line = fact || info?.summary;
  return (
    <article className={"rounded-2xl border-[3px] border-slate-900 bg-white overflow-hidden shadow-[0_4px_0_0_rgba(15,23,42,1)] " + (compact ? "" : "mt-3")} data-testid="place-card">
      {place.kind === "country" ? null : info?.photo ? (
        <img src={`${API}${info.photo}`} alt={`${place.name}`} className="h-36 w-full object-cover border-b-[3px] border-slate-900" loading="lazy" data-testid="place-card-photo" />
      ) : (
        <div className="h-20 w-full border-b-[3px] border-slate-900 bg-gradient-to-br from-sky-200 to-amber-100 grid place-items-center text-4xl" aria-hidden data-testid="place-card-nophoto">
          {info === null ? "…" : "🏙️"}
        </div>
      )}
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-700 inline-flex items-center gap-1"><MapPin size={11} strokeWidth={3} /> {place.kind === "country" ? "Country" : place.capital ? "Capital city" : "City"}</p>
            <h3 className="font-heading text-lg font-bold leading-tight truncate" data-testid="place-card-name">{place.kind === "country" ? `${flagEmoji(place.code)} ${place.name}` : place.name}</h3>
            {place.kind !== "country" && countryName && <p className="text-xs font-bold text-slate-600">{flagEmoji(place.code)} {countryName}</p>}
          </div>
          {onClose && <button type="button" onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-full border-2 border-slate-900 bg-white" data-testid="place-card-close"><X size={14} strokeWidth={3} /></button>}
        </div>
        {line ? (
          <p className="mt-2 text-sm leading-snug" data-testid="place-card-fact">{line}{!fact && info?.source_url && <span className="text-[10px] text-slate-500"> (from Wikipedia)</span>}</p>
        ) : info && place.kind !== "country" && <p className="mt-2 text-xs text-slate-500" data-testid="place-card-nofact">No fact for this city yet — tap the country to keep exploring.</p>}
        {info?.credit && <p className="mt-1 text-[10px] text-slate-400" data-testid="place-card-credit">{info.credit}</p>}
        {onOpenCountry && place.country && (
          <button type="button" onClick={() => onOpenCountry(place.country)} className="mt-3 inline-flex items-center gap-1 min-h-[40px] px-3 rounded-full border-2 border-slate-900 bg-yellow-300 text-sm font-bold shadow-[0_2px_0_0_rgba(15,23,42,1)]" data-testid="place-card-open-country">
            Explore {countryName} <ArrowRight size={14} strokeWidth={3} />
          </button>
        )}
      </div>
    </article>
  );
}
